/**
 * useContractAnalytics Hook
 *
 * Computes derived analytics from payer contracts
 */

import { useMemo } from 'react';
import type { PayerContract, AuthBurdenLevel, CashflowRiskLevel } from '../types';

/**
 * Calculate the next renewal/expiration date for a contract.
 * For auto_renew and evergreen contracts, rolls forward through renewal periods.
 * For renegotiate contracts, uses initial term only.
 */
export function calculateNextExpirationDate(
  effectiveDate: string,
  initialTermYears: number,
  renewalTermYears?: number,
  renewalMechanism?: 'auto_renew' | 'renegotiate' | 'evergreen'
): Date | null {
  try {
    const effective = new Date(effectiveDate);
    const today = new Date();

    if (renewalMechanism === 'auto_renew' || renewalMechanism === 'evergreen') {
      // Roll forward through renewal periods until we're past today
      const termYears = renewalTermYears || initialTermYears;
      let expiration = new Date(effective);
      expiration.setFullYear(expiration.getFullYear() + initialTermYears);

      while (expiration < today) {
        expiration.setFullYear(expiration.getFullYear() + termYears);
      }
      return expiration;
    }

    // Non-renewing: just initial term
    const expiration = new Date(effective);
    expiration.setFullYear(expiration.getFullYear() + initialTermYears);
    return expiration;
  } catch {
    return null;
  }
}

/**
 * Check if contract is expiring within N days
 */
function isExpiringWithinDays(payer: PayerContract, days: number): boolean {
  const c = payer.contract;
  const expiration = calculateNextExpirationDate(
    c.effective_date, c.initial_term_years, c.renewal_term_years, c.renewal_mechanism
  );
  if (!expiration) return false;

  const today = new Date();
  const daysUntilExpiration = Math.ceil(
    (expiration.getTime() - today.getTime()) / (1000 * 60 * 60 * 24)
  );

  return daysUntilExpiration >= 0 && daysUntilExpiration <= days;
}

/**
 * Check if contract is active (not expired)
 */
function isContractActive(payer: PayerContract): boolean {
  const c = payer.contract;
  const expiration = calculateNextExpirationDate(
    c.effective_date, c.initial_term_years, c.renewal_term_years, c.renewal_mechanism
  );
  if (!expiration) return true; // Assume active if we can't calculate

  return expiration >= new Date();
}

/**
 * Calculate auth burden score for a payer
 */
export function calculateAuthBurdenScore(payer: PayerContract): {
  score: number;
  level: AuthBurdenLevel;
} {
  let score = 0;

  // Base score from prior auth requirement
  if (payer.authorization.prior_auth_required) {
    score += 20;
  }

  // Add points for each auth code required
  if (payer.authorization.auth_codes_required) {
    score += payer.authorization.auth_codes_required.length * 5;
  }

  // Add points for visit limits (indicates stricter control)
  if (payer.authorization.visit_limits) {
    score += 15;
  }

  // Add points for reauth cadence (more frequent = higher burden)
  if (payer.authorization.reauth_cadence) {
    if (payer.authorization.reauth_cadence.includes('visit') || payer.authorization.reauth_cadence.includes('session')) {
      score += 20; // Very frequent
    } else if (payer.authorization.reauth_cadence.includes('day')) {
      score += 10; // Daily/weekly
    } else {
      score += 5; // Monthly or less frequent
    }
  }

  // Retrospective auth not allowed = higher burden
  if (payer.authorization.retrospective_auth_allowed === false) {
    score += 10;
  }

  // Determine level
  let level: AuthBurdenLevel;
  if (score >= 50) {
    level = 'high';
  } else if (score >= 25) {
    level = 'medium';
  } else {
    level = 'low';
  }

  return { score, level };
}

/**
 * Calculate cashflow risk score for a payer
 */
export function calculateCashflowRiskScore(payer: PayerContract): {
  score: number;
  level: CashflowRiskLevel;
} {
  let score = 0;

  // Short timely filing = higher risk
  const timelyFilingDays = payer.claims_submission.timely_filing_days;
  if (timelyFilingDays <= 90) {
    score += 30; // Very short
  } else if (timelyFilingDays <= 120) {
    score += 20; // Short
  } else if (timelyFilingDays <= 180) {
    score += 10; // Moderate
  }

  // Short appeal deadlines = higher risk
  if (payer.disputes_and_appeals?.appeal_deadline_days) {
    const appealDays = payer.disputes_and_appeals.appeal_deadline_days;
    if (appealDays <= 30) {
      score += 25; // Very short
    } else if (appealDays <= 60) {
      score += 15; // Short
    } else if (appealDays <= 90) {
      score += 5; // Moderate
    }
  }

  // Aggressive recoupment = higher risk
  if (payer.compliance?.overpayment_return_days) {
    const recoupDays = payer.compliance.overpayment_return_days;
    if (recoupDays <= 30) {
      score += 20; // Very aggressive
    } else if (recoupDays <= 60) {
      score += 10; // Aggressive
    }
  }

  // Long lookback window = higher risk
  if (payer.compliance?.overpayment_recovery_method) {
    if (payer.compliance.overpayment_recovery_method.toLowerCase().includes('year')) {
      score += 15;
    }
  }

  // Determine level
  let level: CashflowRiskLevel;
  if (score >= 60) {
    level = 'high';
  } else if (score >= 30) {
    level = 'medium';
  } else {
    level = 'low';
  }

  return { score, level };
}

/**
 * Hook to compute analytics from payer contracts
 */
export function useContractAnalytics(payers: PayerContract[]) {
  return useMemo(() => {
    const activeContracts = payers.filter(p =>
      isContractActive(p)
    ).length;

    const renewalsInNext90Days = payers.filter(p =>
      isExpiringWithinDays(p, 90)
    ).length;

    const missingFeeSchedules = payers.filter(p => {
      const hasRates = p.reimbursement.rates && p.reimbursement.rates.length > 0;
      const missingInDocs =
        p.contract.missing_documents?.some(
          doc => doc.toLowerCase().includes('fee') || doc.toLowerCase().includes('schedule')
        ) || false;
      return !hasRates || missingInDocs;
    }).length;

    const highRiskFlags = payers.reduce((count, p) => {
      const criticalHigh = (p.audit_flags || []).filter(
        flag => flag.severity === 'critical' || flag.severity === 'high'
      ).length;
      return count + criticalHigh;
    }, 0);

    // Calculate scores for each payer
    const payerScores = payers.map(payer => ({
      payer,
      authBurden: calculateAuthBurdenScore(payer),
      cashflowRisk: calculateCashflowRiskScore(payer),
    }));

    return {
      totalContracts: payers.length,
      activeContracts,
      renewalsInNext90Days,
      missingFeeSchedules,
      highRiskFlags,
      payerScores,
    };
  }, [payers]);
}
